import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { Formik, Form, FieldArray } from "formik";

export const emptyFeesData = () => ({
  annual_fee_enabled: false,
  annual_fee: "",
  setup_fee_enabled: false,
  setup_fee: "",
  chargeback_fee_enabled: false,
  chargeback_fee: "",
  refund_fee_enabled: false,
  refund_fee: "",
  reprogramming_fee_enabled: false,
  reprogramming_fee: "",
  physical_terminal_enabled: false,
  physical_terminal_per_unit: "",
  physical_terminal_num_units: "",
  physical_terminal_total: "",
  security_deposit_terminal_enabled: false,
  security_deposit_terminal: "",
  other_fees_enabled: false,
  other_fees: [
    { description: "", amount: "" },
    { description: "", amount: "" },
    { description: "", amount: "" },
  ],
  settlement_cycle_enabled: false,
  settlement_cycle_type: "daily",
  settlement_cycle_others: "",
  security_deposit_enabled: false,
  security_deposit_percentage: "",
  security_deposit_projected_revenue: "",
});

const FeeRow = ({ label, sublabel, enabledField, amountField, values, setFieldValue, isSubmitting, readOnly }) => (
  <Grid container alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
    <Grid item xs={12} sm={6}>
      <FormControlLabel
        control={
          <Checkbox
            checked={!!values[enabledField]}
            onChange={(e) => setFieldValue(enabledField, e.target.checked)}
            disabled={isSubmitting || readOnly}
          />
        }
        label={
          <Box>
            <Typography variant="body2" fontWeight="medium">{label}</Typography>
            {sublabel && (
              <Typography variant="caption" color="text.secondary" display="block">
                {sublabel}
              </Typography>
            )}
          </Box>
        }
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        size="small"
        fullWidth
        type="number"
        inputProps={{ step: "any", min: 0 }}
        disabled={!values[enabledField] || isSubmitting || readOnly}
        value={values[amountField]}
        onChange={(e) => setFieldValue(amountField, e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
      />
    </Grid>
  </Grid>
);

const Step4Fees = ({ formData, onNext, onSkip, onBack, isSubmitting, readOnly }) => {
  const initialValues = formData && Object.keys(formData).length > 0 ? formData : emptyFeesData();

  const handleSubmit = (values, actions) => {
    const totalCost = values.physical_terminal_enabled
      ? String((parseFloat(values.physical_terminal_per_unit || 0) * parseFloat(values.physical_terminal_num_units || 0)).toFixed(2))
      : values.physical_terminal_total;
    onNext({ ...values, physical_terminal_total: totalCost }, actions);
  };

  return (
    <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
      {({ values, setFieldValue, submitForm }) => (
        <Form>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
              OTHER FEES AND SETTLEMENT ARRANGEMENT
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Check and fill in the applicable fees for this onboarding application.
            </Typography>

            {/* Individual Fees */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 2, color: "text.secondary", textTransform: "uppercase", letterSpacing: 1 }}>
                  Fees
                </Typography>
                <FeeRow
                  label="Annual Fee"
                  sublabel="Per Annum (exclusive of GST)"
                  enabledField="annual_fee_enabled"
                  amountField="annual_fee"
                  values={values}
                  setFieldValue={setFieldValue}
                  isSubmitting={isSubmitting}
                  readOnly={readOnly}
                />
                <FeeRow
                  label="Set-up Fee"
                  sublabel="One-Time (exclusive of GST)"
                  enabledField="setup_fee_enabled"
                  amountField="setup_fee"
                  values={values}
                  setFieldValue={setFieldValue}
                  isSubmitting={isSubmitting}
                  readOnly={readOnly}
                />
                <FeeRow
                  label="Chargeback Fee"
                  sublabel="Per Successful Chargeback (applies to credit cards only)(exclusive of GST)"
                  enabledField="chargeback_fee_enabled"
                  amountField="chargeback_fee"
                  values={values}
                  setFieldValue={setFieldValue}
                  isSubmitting={isSubmitting}
                  readOnly={readOnly}
                />
                <FeeRow
                  label="Refund"
                  sublabel="Administrative Fee Per Successful Refund (exclusive of GST)"
                  enabledField="refund_fee_enabled"
                  amountField="refund_fee"
                  values={values}
                  setFieldValue={setFieldValue}
                  isSubmitting={isSubmitting}
                  readOnly={readOnly}
                />
                <FeeRow
                  label="Reprogramming Fee"
                  sublabel="(exclusive of GST)"
                  enabledField="reprogramming_fee_enabled"
                  amountField="reprogramming_fee"
                  values={values}
                  setFieldValue={setFieldValue}
                  isSubmitting={isSubmitting}
                  readOnly={readOnly}
                />
              </CardContent>
            </Card>

            {/* Physical Terminal */}
            <Card
              variant="outlined"
              sx={{ mb: 3, borderColor: "secondary.main", borderWidth: 2 }}
            >
              <CardContent>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, color: "secondary.main", textTransform: "uppercase", letterSpacing: 1 }}
                >
                  Physical Terminal
                </Typography>

                <Grid container alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!values.physical_terminal_enabled}
                          onChange={(e) => setFieldValue("physical_terminal_enabled", e.target.checked)}
                          disabled={isSubmitting || readOnly}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            Physical Terminal (Per Unit)
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            (exclusive of GST)
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      label="$ Per Terminal"
                      inputProps={{ step: "any", min: 0 }}
                      disabled={!values.physical_terminal_enabled || isSubmitting || readOnly}
                      value={values.physical_terminal_per_unit}
                      onChange={(e) => {
                        setFieldValue("physical_terminal_per_unit", e.target.value);
                        const total = (parseFloat(e.target.value || 0) * parseFloat(values.physical_terminal_num_units || 0)).toFixed(2);
                        setFieldValue("physical_terminal_total", total);
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="No. of Terminal(s)"
                      type="number"
                      inputProps={{ min: 0 }}
                      disabled={!values.physical_terminal_enabled || isSubmitting || readOnly}
                      value={values.physical_terminal_num_units}
                      onChange={(e) => {
                        setFieldValue("physical_terminal_num_units", e.target.value);
                        const total = (parseFloat(values.physical_terminal_per_unit || 0) * parseFloat(e.target.value || 0)).toFixed(2);
                        setFieldValue("physical_terminal_total", total);
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">Unit(s)</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Total Cost of Terminal(s)"
                      disabled
                      value={
                        values.physical_terminal_enabled
                          ? (parseFloat(values.physical_terminal_per_unit || 0) * parseFloat(values.physical_terminal_num_units || 0)).toFixed(2)
                          : (values.physical_terminal_total || "")
                      }
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        endAdornment: <InputAdornment position="end">(excl. GST)</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!values.security_deposit_terminal_enabled}
                          onChange={(e) => setFieldValue("security_deposit_terminal_enabled", e.target.checked)}
                          disabled={isSubmitting || readOnly}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Limited Time Offer: Refundable Security Deposit for terminal(s)
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      inputProps={{ step: "any", min: 0 }}
                      disabled={!values.security_deposit_terminal_enabled || isSubmitting || readOnly}
                      value={values.security_deposit_terminal}
                      onChange={(e) => setFieldValue("security_deposit_terminal", e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Other Fees */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!values.other_fees_enabled}
                      onChange={(e) => setFieldValue("other_fees_enabled", e.target.checked)}
                      disabled={isSubmitting || readOnly}
                    />
                  }
                  label={<Typography variant="body2" fontWeight="medium">Other Fees</Typography>}
                />
                {values.other_fees_enabled && (
                  <FieldArray name="other_fees">
                    {() => (
                      <Box sx={{ mt: 1.5, pl: 4 }}>
                        {(values.other_fees || []).map((fee, index) => (
                          <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                            <Grid item xs={8}>
                              <TextField
                                size="small"
                                fullWidth
                                label={`Description ${index + 1}`}
                                disabled={isSubmitting || readOnly}
                                value={fee.description}
                                onChange={(e) => setFieldValue(`other_fees[${index}].description`, e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                size="small"
                                fullWidth
                                type="number"
                                label="Amount"
                                inputProps={{ step: "any", min: 0 }}
                                disabled={isSubmitting || readOnly}
                                value={fee.amount}
                                onChange={(e) => setFieldValue(`other_fees[${index}].amount`, e.target.value)}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                              />
                            </Grid>
                          </Grid>
                        ))}
                      </Box>
                    )}
                  </FieldArray>
                )}
              </CardContent>
            </Card>

            {/* Settlement Cycle */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!values.settlement_cycle_enabled}
                      onChange={(e) => setFieldValue("settlement_cycle_enabled", e.target.checked)}
                      disabled={isSubmitting || readOnly}
                    />
                  }
                  label={<Typography variant="body2" fontWeight="medium">Settlement Cycle</Typography>}
                />
                {values.settlement_cycle_enabled && (
                  <Box sx={{ pl: 4, mt: 1 }}>
                    <RadioGroup
                      row
                      value={values.settlement_cycle_type || "daily"}
                      onChange={(e) => setFieldValue("settlement_cycle_type", e.target.value)}
                    >
                      <FormControlLabel
                        value="daily"
                        control={<Radio disabled={isSubmitting || readOnly} size="small" />}
                        label="Daily"
                      />
                      <FormControlLabel
                        value="weekly"
                        control={<Radio disabled={isSubmitting || readOnly} size="small" />}
                        label="Weekly"
                      />
                      <FormControlLabel
                        value="others"
                        control={<Radio disabled={isSubmitting || readOnly} size="small" />}
                        label="Others, Please specify:"
                      />
                    </RadioGroup>
                    {values.settlement_cycle_type === "others" && (
                      <TextField
                        size="small"
                        sx={{ mt: 1, maxWidth: 300 }}
                        fullWidth
                        label="Please specify"
                        disabled={isSubmitting || readOnly}
                        value={values.settlement_cycle_others}
                        onChange={(e) => setFieldValue("settlement_cycle_others", e.target.value)}
                      />
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Security Deposit */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!values.security_deposit_enabled}
                      onChange={(e) => setFieldValue("security_deposit_enabled", e.target.checked)}
                      disabled={isSubmitting || readOnly}
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight="medium">
                      Security Deposit (subject to periodic review)
                    </Typography>
                  }
                />
                {values.security_deposit_enabled && (
                  <Box sx={{ pl: 4, mt: 1.5 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          size="small"
                          fullWidth
                          type="number"
                          label="Percentage of daily settlement to be withheld"
                          inputProps={{ step: "any", min: 0, max: 100 }}
                          disabled={isSubmitting || readOnly}
                          value={values.security_deposit_percentage}
                          onChange={(e) => setFieldValue("security_deposit_percentage", e.target.value)}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          size="small"
                          fullWidth
                          type="number"
                          label="Projected monthly sales revenue (in SGD)"
                          inputProps={{ step: "any", min: 0 }}
                          disabled={isSubmitting || readOnly}
                          value={values.security_deposit_projected_revenue}
                          onChange={(e) => setFieldValue("security_deposit_projected_revenue", e.target.value)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Navigation */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
              <Button variant="outlined" onClick={onBack} disabled={isSubmitting || readOnly}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={submitForm}
                disabled={isSubmitting || readOnly}
              >
                {isSubmitting ? "Saving..." : "Submit for Approval"}
              </Button>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default Step4Fees;
